import { useEffect, useMemo, useState } from 'react';
import { BookOpen, ExternalLink, Search, Sparkles, Video, X } from 'lucide-react';
import clsx from 'clsx';
import { usePublishedHelpQuery } from '@/shared/help/use-help';
import { EMPTY_HELP, type HelpArticle } from '@/shared/help/types';

type Tab = 'articles' | 'videos' | 'news';

const TABS: { key: Tab; label: string; icon: typeof BookOpen }[] = [
    { key: 'articles', label: 'Articles',    icon: BookOpen },
    { key: 'videos',   label: 'Videos',      icon: Video },
    { key: 'news',     label: "What's new",  icon: Sparkles },
];

function fmtDay(d: string): string {
    const parsed = new Date(`${d}T00:00:00`);
    return Number.isNaN(parsed.getTime())
        ? d
        : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function groupByCategory(articles: HelpArticle[]): [string, HelpArticle[]][] {
    const map = new Map<string, HelpArticle[]>();
    for (const a of articles) {
        const list = map.get(a.category);
        if (list) list.push(a);
        else map.set(a.category, [a]);
    }
    return [...map.entries()];
}

/**
 * Client-facing help centre — the read side of what the super admin publishes
 * in Admin → Help controls. Opened from the "?" button in the topbar.
 */
export function HelpPanel({ onClose }: { onClose: () => void }) {
    const [tab, setTab] = useState<Tab>('articles');
    const [search, setSearch] = useState('');
    const { data: content = EMPTY_HELP, isLoading, isError } = usePublishedHelpQuery();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const articles = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return content.articles;
        return content.articles.filter(
            (a) => a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q) || a.category.toLowerCase().includes(q),
        );
    }, [content.articles, search]);

    const empty = 'px-1 py-10 text-center text-sm text-ink-300';

    return (
        <div className="fixed inset-0 z-[900] flex" onClick={onClose}>
            <div className="flex-1 bg-brand-navy/30" />
            <aside
                className="flex w-full max-w-md flex-col bg-white shadow-pop"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Help centre"
            >
                <div className="flex items-start justify-between border-b border-ink-100 p-5">
                    <div>
                        <h2 className="font-display text-lg font-bold text-brand-navy">Help centre</h2>
                        <p className="mt-0.5 text-xs text-ink-500">Guides, walkthroughs, and what shipped recently.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close help"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-ink-50"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex gap-1 border-b border-ink-100 px-3">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setTab(key)}
                            className={clsx(
                                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 font-ui text-sm font-semibold transition',
                                tab === key
                                    ? 'border-brand-navy text-brand-navy'
                                    : 'border-transparent text-ink-500 hover:text-brand-navy',
                            )}
                        >
                            <Icon className="h-4 w-4" /> {label}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {isLoading && <div className={empty}>Loading help…</div>}
                    {isError && !isLoading && (
                        <div className={empty}>Couldn’t load help content. Try again shortly.</div>
                    )}
                    {!isLoading && !isError && (<>
                    {tab === 'articles' && (
                        <>
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search help articles…"
                                    className="h-9 w-full rounded-lg border border-ink-100 bg-ink-50 pl-9 pr-3 text-sm focus:border-brand-sky-500 focus:bg-white focus:outline-none"
                                />
                            </div>
                            {articles.length === 0 ? (
                                <div className={empty}>No articles match that search.</div>
                            ) : (
                                <div className="space-y-5">
                                    {groupByCategory(articles).map(([cat, items]) => (
                                        <section key={cat}>
                                            <div className="mb-2 font-ui text-[10px] font-semibold uppercase tracking-wider text-brand-sky-500">
                                                {cat}
                                            </div>
                                            <div className="space-y-2">
                                                {items.map((a) => (
                                                    <details key={a.id} className="rounded-lg border border-ink-100 p-3 [&_summary]:list-none">
                                                        <summary className="cursor-pointer text-sm font-semibold text-brand-navy">
                                                            {a.title}
                                                        </summary>
                                                        <p className="mt-2 text-[13px] leading-relaxed text-ink-700">{a.body}</p>
                                                    </details>
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {tab === 'videos' && (
                        content.videos.length === 0 ? (
                            <div className={empty}>No videos published yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {content.videos.map((v) => {
                                    const body = (
                                        <>
                                            <div className="flex h-8 w-12 shrink-0 items-center justify-center rounded bg-brand-navy text-white">
                                                <Video className="h-3.5 w-3.5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-brand-navy">{v.title}</div>
                                                {v.duration && <div className="text-[11px] text-ink-500">{v.duration}</div>}
                                            </div>
                                            {v.url && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-300" />}
                                        </>
                                    );
                                    return v.url ? (
                                        <a
                                            key={v.id}
                                            href={v.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-3 rounded-lg border border-ink-100 p-3 transition hover:bg-ink-50"
                                        >
                                            {body}
                                        </a>
                                    ) : (
                                        <div key={v.id} className="flex items-center gap-3 rounded-lg border border-ink-100 p-3 opacity-70">
                                            {body}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}

                    {tab === 'news' && (
                        content.news.length === 0 ? (
                            <div className={empty}>Nothing new to report.</div>
                        ) : (
                            <div className="space-y-3">
                                {content.news.map((n) => (
                                    <div key={n.id} className="rounded-lg border border-ink-100 p-3">
                                        <div className="font-ui text-[10px] font-semibold uppercase tracking-wider text-brand-sky-500">
                                            {fmtDay(n.published_on)}
                                        </div>
                                        <div className="mt-0.5 text-sm font-semibold text-brand-navy">{n.title}</div>
                                        <p className="mt-1 text-[13px] leading-relaxed text-ink-700">{n.body}</p>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                    </>)}
                </div>
            </aside>
        </div>
    );
}
